import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Form, Row } from "@ster/ster-toolkit";
import { Form as AntForm, Col, FormInstance } from "antd";
import { NamePath } from "antd/lib/form/interface";

import { settingsSelector } from "../../../../shared/selectors";
import { useAppSelector } from "../../../../utils/hooks";
import { BudgetBonusFormSchema } from "../models";
import BonusInput from "./BonusInput";
import BudgetInput from "./BudgetInput";
import useBonusValidationGenerator, {
  BonusValidationConfiguration,
} from "./useBonusValidationGenerator";
import useBudgetValidationGenerator, {
  BudgetValidationConfiguration,
} from "./useBudgetValidationGenerator";

export type BudgetBonusValidationConfiguration = {
  bonus: BonusValidationConfiguration;
  budget: BudgetValidationConfiguration;
};

const BudgetBonusFormSection = <T extends BudgetBonusFormSchema>({
  form,
  disabled,
  budgetEditable,
  bonusEditable,
  rules: { bonus: bonusRules, budget: budgetRules },
}: {
  form: FormInstance<T>;
  disabled?: boolean; // completely disables all input
  budgetEditable: boolean; // false when budget is managed through another setting for this instance, otherwise true
  bonusEditable: boolean; // false when bonus is managed through another setting for this instance, otherwise true
  rules: BudgetBonusValidationConfiguration;
}) => {
  const i18n = useLingui();
  const settings = useAppSelector(settingsSelector);
  const bonusEnabled = !!settings?.bonusEnabled;

  const budget = AntForm.useWatch("budget", form);

  const { createWithConfig: createBudgetRules } =
    useBudgetValidationGenerator();
  const { createWithConfig: createBonusRules } = useBonusValidationGenerator();

  const budgetValidationRules = createBudgetRules(budgetRules);
  const bonusValidationRules = createBonusRules(bonusRules);

  const getValidationStatus = (name: NamePath) => {
    const error = form.getFieldError(name);
    return error.length > 0 ? "error" : "";
  };

  const getValidationMessage = (name: NamePath) => {
    const error = form.getFieldError(name);
    return error.length > 0 ? error[0] : null;
  };

  // Bonus is editable
  return bonusEnabled ? (
    <>
      <Row className="inlineWidthLabels minimalAdditional" gutter={[16, 0]}>
        <Col span={8}>
          <Form.Item<BudgetBonusFormSchema>
            label={i18n._(t`Bruto`)}
            name="budget"
            rules={budgetValidationRules}
            dependencies={["breakSelection"]}
            validateStatus={getValidationStatus("budget")}
            help=""
          >
            <BudgetInput disabled={disabled || budgetEditable === false} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<BudgetBonusFormSchema>
            label={i18n._(t`Bonus`)}
            name="bonus"
            rules={bonusValidationRules}
            dependencies={["budget"]}
            validateStatus={getValidationStatus("bonus")}
            help=""
          >
            <BonusInput
              disabled={disabled || bonusEditable === false}
              budget={budget ?? 0}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<BudgetBonusFormSchema>
            label={i18n._(t`Netto`)}
            name="bonus"
            rules={bonusValidationRules}
            dependencies={["budget"]}
            validateStatus={getValidationStatus("bonus")}
            help=""
          >
            <BonusInput
              disabled={disabled || bonusEditable === false}
              asNetto
              budget={budget ?? 0}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <div className="ant-form-item-explain-error">
            {getValidationMessage("budget") ?? getValidationMessage("bonus")}
          </div>
        </Col>
      </Row>
    </>
  ) : (
    <Form.Item<BudgetBonusFormSchema>
      name="budget"
      rules={budgetValidationRules}
      noStyle
    >
      <BudgetInput />
    </Form.Item>
  );
};

export default BudgetBonusFormSection;
